import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  TextField,
  Typography,
  Tooltip,
  Popover,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableChartIcon from "@mui/icons-material/TableChart";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import TableCardv2 from "../../components/TableCardv2";
import {
  listarJobsProcessoRelatorio,
  buscarJobProcessoRelatorioPorId,
  patchPrioridadeJobProcessoRelatorio,
  subirPrioridadeJobProcessoRelatorio,
  descerPrioridadeJobProcessoRelatorio,
  postReexecutarJobProcessoRelatorio,
} from "../../services/api-planejamento-tributario.service";
import {
  usePermissoes,
  CHAVES_PROCESSAMENTO_EDIT,
} from "../../config/Context/PermissoesContext";

const STATUS_LABEL = {
  pendente: "Pendente",
  em_execucao: "Em execução",
  concluido: "Concluído",
  erro: "Erro",
  cancelado: "Cancelado",
};

const STATUS_VALIDOS = new Set([
  "pendente",
  "em_execucao",
  "concluido",
  "erro",
  "cancelado",
]);

/** Mesmas chaves exibidas na tabela (valor interno → API). */
const COLUNAS_FILTRO = [
  { value: "", label: "Coluna" },
  { value: "cnpj", label: "CNPJ" },
  { value: "periodo", label: "Período" },
  { value: "status", label: "Status" },
];

function formatarCnpj(cnpj) {
  if (!cnpj || String(cnpj).length !== 14) return cnpj || "—";
  const d = String(cnpj).replace(/\D/g, "");
  if (d.length !== 14) return cnpj;
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

function chipStatusCompact(status) {
  const label = STATUS_LABEL[status] || status;
  const map = {
    pendente: { bgcolor: "#fff3e0", color: "#e65100" },
    em_execucao: { bgcolor: "#e3f2fd", color: "#1565c0" },
    concluido: { bgcolor: "#e8f5e9", color: "#2e7d32" },
    erro: { bgcolor: "#ffebee", color: "#c62828" },
    cancelado: { bgcolor: "#f5f5f5", color: "#757575" },
  };
  const sx = map[status] || map.cancelado;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        maxWidth: "5.15rem",
        fontWeight: "bold",
        bgcolor: sx.bgcolor,
        color: sx.color,
        "& .MuiChip-label": {
          px: 0.5,
          fontSize: "0.68rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    />
  );
}

function normalizarStatusDigitado(texto) {
  if (!texto || !String(texto).trim()) return "";
  let t = String(texto).trim().toLowerCase();
  t = t.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  t = t.replace(/\s+/g, "_");
  return t;
}

function parseDataFlex(s) {
  const t = String(s).trim();
  const br = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (br) {
    const d = new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

function inicioDoDia(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function fimDoDia(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/**
 * Monta query da API conforme coluna da tabela + texto de busca.
 */
function montarParamsPorColuna(coluna, texto) {
  const raw = String(texto || "").trim();
  if (!coluna || !raw) return {};

  switch (coluna) {
    case "cnpj": {
      const d = raw.replace(/\D/g, "");
      if (d.length !== 14) return {};
      return { cnpj: d };
    }

    case "periodo": {
      const partesData = raw
        .split(/\s*(?:-|–|—|até|ate|a)\s*/i)
        .map((s) => s.trim())
        .filter(Boolean);
      if (partesData.length === 2) {
        const d1 = parseDataFlex(partesData[0]);
        const d2 = parseDataFlex(partesData[1]);
        if (d1 && d2) {
          const ini = inicioDoDia(d1);
          const fim = fimDoDia(d2);
          if (ini <= fim) {
            return {
              periodo_busca_de: ini.toISOString(),
              periodo_busca_ate: fim.toISOString(),
            };
          }
        }
        return {};
      }
      if (partesData.length === 1) {
        const d = parseDataFlex(partesData[0]);
        if (d) {
          const ini = inicioDoDia(d);
          const fim = fimDoDia(d);
          return {
            periodo_busca_de: ini.toISOString(),
            periodo_busca_ate: fim.toISOString(),
          };
        }
      }
      return {};
    }

    case "status": {
      const t = normalizarStatusDigitado(raw);
      if (t && STATUS_VALIDOS.has(t)) return { status: t };
      return {};
    }

    default:
      return {};
  }
}

const ProcessamentoRelatorio = () => {
  const { pode } = usePermissoes();
  const podeEditar = pode(CHAVES_PROCESSAMENTO_EDIT);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [colunaFiltro, setColunaFiltro] = useState("");
  const [buscaValor, setBuscaValor] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  /** TXM-325 item 06 (Parte A) — deep-link `?apuracao_id&cnpj` vindo da listagem de apurações. */
  const [apuracaoIdFiltro, setApuracaoIdFiltro] = useState(
    () => searchParams.get("apuracao_id") || ""
  );
  const [cnpjApuracaoFiltro, setCnpjApuracaoFiltro] = useState(
    () => searchParams.get("cnpj") || ""
  );

  const limparFiltroApuracao = () => {
    setApuracaoIdFiltro("");
    setCnpjApuracaoFiltro("");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("apuracao_id");
      next.delete("cnpj");
      return next;
    });
    setPage(1);
  };

  const [modalAberto, setModalAberto] = useState(false);
  const [jobDetalhe, setJobDetalhe] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

  const [snackbar, setSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const [priorAnchorEl, setPriorAnchorEl] = useState(null);
  const [prioridadeEditId, setPrioridadeEditId] = useState(null);
  const [prioridadeInput, setPrioridadeInput] = useState("");

  const initialLoadDoneRef = useRef(false);
  const abrirDetalheRef = useRef(() => {});

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbar(true);
  };

  const abrirDetalhe = async (rowOriginal) => {
    setModalAberto(true);
    setJobDetalhe(null);
    setCarregandoDetalhe(true);
    try {
      const res = await buscarJobProcessoRelatorioPorId(rowOriginal.id);
      if (res?.sucesso && res.item) {
        setJobDetalhe(res.item);
      } else {
        setJobDetalhe(rowOriginal);
      }
    } catch {
      setJobDetalhe(rowOriginal);
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  abrirDetalheRef.current = abrirDetalhe;

  const fecharModal = () => {
    setModalAberto(false);
    setJobDetalhe(null);
  };

  useEffect(() => {
    let cancelado = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = {
          limit: String(limit),
          offset: String((page - 1) * limit),
        };

        if (apuracaoIdFiltro) {
          params.apuracao_id = apuracaoIdFiltro;
          if (cnpjApuracaoFiltro) params.cnpj = cnpjApuracaoFiltro;
        }

        Object.assign(params, montarParamsPorColuna(colunaFiltro, buscaValor));

        const res = await listarJobsProcessoRelatorio(params);
        if (cancelado) return;
        if (res?.sucesso) {
          const raw = Array.isArray(res.itens) ? res.itens : [];

          const reexecutarJob = async (jobId) => {
            try {
              await postReexecutarJobProcessoRelatorio(jobId);
              if (cancelado) return;
              showSnackbar("Relatório reenfileirado.", "success");
              setRefreshTick((t) => t + 1);
            } catch (e) {
              if (cancelado) return;
              const msg =
                e?.response?.data?.mensagem ||
                e?.message ||
                "Erro ao reexecutar.";
              showSnackbar(msg, "error");
            }
          };

          const formatted = raw.map((item) => {
            const nome =
              item.nomeRelatorio && String(item.nomeRelatorio).trim()
                ? String(item.nomeRelatorio).trim()
                : "—";
            const podeMudarPosicao = item.status === "pendente";
            const podeReexecutar = item.status === "erro";
            return {
              ...item,
              nomeRelatorioCell:
                nome === "—" ? (
                  nome
                ) : (
                  <Tooltip title={nome} arrow placement="top">
                    <span
                      style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                      }}
                    >
                      {nome}
                    </span>
                  </Tooltip>
                ),
              prioridade_fmt: item.prioridade != null ? String(item.prioridade) : "—",
              cnpj_fmt: formatarCnpj(item.cnpj),
              periodo: `${item.dataInicial || "—"} — ${item.dataFinal || "—"}`,
              statusDisplay: chipStatusCompact(item.status),
              btn: [
                ...(podeEditar
                  ? [
                      {
                        icon: <SwapVertOutlinedIcon fontSize="small" />,
                        title: "Mudar posição na fila",
                        disabled: !podeMudarPosicao,
                        onClick: (e) => {
                          if (!podeMudarPosicao) return;
                          setPriorAnchorEl(e.currentTarget);
                          setPrioridadeEditId(item.id);
                          setPrioridadeInput(String(item.prioridade ?? ""));
                        },
                      },
                      {
                        icon: <ReplayIcon fontSize="small" />,
                        title: podeReexecutar
                          ? "Reexecutar relatório"
                          : "Só é possível reexecutar quando o job estiver com erro",
                        disabled: !podeReexecutar,
                        onClick: () => {
                          if (!podeReexecutar) return;
                          void reexecutarJob(item.id);
                        },
                      },
                    ]
                  : []),
                {
                  icon: <InfoOutlinedIcon fontSize="small" />,
                  title: "Detalhes",
                  onClick: () => abrirDetalheRef.current(item),
                },
              ],
            };
          });
          setFilteredData(formatted);
          setTotalJobs(typeof res.total === "number" ? res.total : 0);
          initialLoadDoneRef.current = true;
        } else {
          setFilteredData([]);
          setTotalJobs(0);
          showSnackbar(res?.mensagem || "Não foi possível carregar os jobs.", "error");
        }
      } catch (e) {
        if (cancelado) return;
        const msg =
          e?.response?.data?.mensagem ||
          e?.message ||
          "Erro ao consultar processamento.";
        showSnackbar(msg, "error");
        setFilteredData([]);
        setTotalJobs(0);
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    run();
    return () => {
      cancelado = true;
    };
  }, [
    page,
    limit,
    colunaFiltro,
    buscaValor,
    refreshTick,
    apuracaoIdFiltro,
    cnpjApuracaoFiltro,
  ]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleColunaChange = (value) => {
    setColunaFiltro(value);
    setPage(1);
  };

  const handleBuscaValor = (value) => {
    setBuscaValor(value);
    setPage(1);
  };

  const paginationConfig = {
    serverSideTotal: totalJobs,
    serverSidePage: page,
    serverSideRowsPerPage: limit,
    onServerPageChange: handlePageChange,
    onServerRowsPerPageChange: handleRowsPerPageChange,
  };

  const columns = [
    { key: "cnpj_fmt", label: "CNPJ", width: "13%" },
    { key: "periodo", label: "Período", width: "17%" },
    { key: "nomeRelatorioCell", label: "Relatório", width: "26%", align: "left" },
    { key: "prioridade_fmt", label: "Prioridade", width: "7.5rem" },
    { key: "statusDisplay", label: "Status", width: "5.5rem" },
  ];

  const header = {
    icon: <TableChartIcon sx={{ fontSize: 30 }} />,
    title: "Processamento",
  };

  const textoDetalheModal = () => {
    if (carregandoDetalhe || !jobDetalhe) return "Carregando…";
    const linhas = [];
    if (jobDetalhe.nomeRelatorio) {
      linhas.push(`relatório: ${jobDetalhe.nomeRelatorio}`);
      linhas.push("");
    }
    if (jobDetalhe.prioridade != null) {
      linhas.push(`prioridade: ${jobDetalhe.prioridade}`);
      linhas.push("");
    }
    if (jobDetalhe.mensagemErro) {
      linhas.push("--- Erro ---");
      linhas.push(jobDetalhe.mensagemErro);
      linhas.push("");
    }
    if (jobDetalhe.parametrosExecucao != null) {
      linhas.push("--- parametros_execucao (JSON) ---");
      try {
        linhas.push(JSON.stringify(jobDetalhe.parametrosExecucao, null, 2));
      } catch {
        linhas.push(String(jobDetalhe.parametrosExecucao));
      }
      linhas.push("");
    }
    if (jobDetalhe.queryExecutavel) {
      linhas.push("--- query_executavel ---");
      linhas.push(jobDetalhe.queryExecutavel);
      linhas.push("");
    }
    if (linhas.length === 0) {
      return "Nenhuma mensagem de erro ou log adicional neste job.";
    }
    return linhas.join("\n");
  };

  return (
    <div style={{ padding: "20px" }}>
      {apuracaoIdFiltro && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          data-testid="alert-filtro-apuracao"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={limparFiltroApuracao}
              data-testid="btn-limpar-filtro-apuracao"
            >
              Limpar filtro
            </Button>
          }
        >
          Mostrando apenas os jobs da apuração #{apuracaoIdFiltro}
          {cnpjApuracaoFiltro ? ` — CNPJ ${formatarCnpj(cnpjApuracaoFiltro)}` : ""}.
        </Alert>
      )}
      <TableCardv2
        header={header}
        data={filteredData}
        columns={columns}
        loading={loading && !initialLoadDoneRef.current}
        loadingTable={loading && initialLoadDoneRef.current}
        complementaryHeaderBottom={
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="proc-coluna">Coluna</InputLabel>
              <Select
                labelId="proc-coluna"
                label="Coluna"
                value={colunaFiltro}
                onChange={(e) => handleColunaChange(e.target.value)}
              >
                {COLUNAS_FILTRO.map((o) => (
                  <MenuItem key={o.value || "none"} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Buscar"
              value={buscaValor}
              onChange={(e) => handleBuscaValor(e.target.value)}
              disabled={!colunaFiltro}
              sx={{ minWidth: 260, maxWidth: 400 }}
              inputProps={{ "data-testid": "input-busca-processamento" }}
            />
            <Tooltip title="Atualizar">
              <IconButton
                size="small"
                onClick={() => setRefreshTick((t) => t + 1)}
                sx={{ color: "#18316e" }}
                aria-label="Atualizar"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
        paginationConfig={paginationConfig}
      />

      <Popover
        open={Boolean(priorAnchorEl)}
        anchorEl={priorAnchorEl}
        onClose={() => {
          setPriorAnchorEl(null);
          setPrioridadeEditId(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#18316e" }}>
            Nova prioridade
          </Typography>
          <TextField
            size="small"
            fullWidth
            type="number"
            label="Valor"
            value={prioridadeInput}
            onChange={(e) => setPrioridadeInput(e.target.value)}
            inputProps={{ min: 1 }}
          />
          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, mt: 1.5 }}>
            <Tooltip title="Subir uma posição na fila" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={!prioridadeEditId}
                  onClick={async () => {
                    if (!prioridadeEditId) return;
                    try {
                      await subirPrioridadeJobProcessoRelatorio(prioridadeEditId);
                      showSnackbar("Prioridade atualizada.", "success");
                      setRefreshTick((t) => t + 1);
                    } catch (e) {
                      const msg =
                        e?.response?.data?.mensagem ||
                        e?.message ||
                        "Erro ao subir na fila.";
                      showSnackbar(msg, "error");
                    }
                  }}
                  sx={{ color: "#18316e" }}
                  aria-label="Subir uma posição na fila"
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Descer uma posição na fila" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={!prioridadeEditId}
                  onClick={async () => {
                    if (!prioridadeEditId) return;
                    try {
                      await descerPrioridadeJobProcessoRelatorio(prioridadeEditId);
                      showSnackbar("Prioridade atualizada.", "success");
                      setRefreshTick((t) => t + 1);
                    } catch (e) {
                      const msg =
                        e?.response?.data?.mensagem ||
                        e?.message ||
                        "Erro ao descer na fila.";
                      showSnackbar(msg, "error");
                    }
                  }}
                  sx={{ color: "#18316e" }}
                  aria-label="Descer uma posição na fila"
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
            <Button
              size="small"
              onClick={() => {
                setPriorAnchorEl(null);
                setPrioridadeEditId(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ bgcolor: "#18316e" }}
              onClick={async () => {
                if (!prioridadeEditId) return;
                const n = parseInt(String(prioridadeInput).trim(), 10);
                if (!Number.isFinite(n)) {
                  showSnackbar("Informe um número válido.", "error");
                  return;
                }
                try {
                  await patchPrioridadeJobProcessoRelatorio(prioridadeEditId, {
                    prioridade: n,
                  });
                  showSnackbar("Prioridade atualizada.", "success");
                  setPriorAnchorEl(null);
                  setPrioridadeEditId(null);
                  setRefreshTick((t) => t + 1);
                } catch (e) {
                  const msg =
                    e?.response?.data?.mensagem ||
                    e?.message ||
                    "Erro ao definir prioridade.";
                  showSnackbar(msg, "error");
                }
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Popover>

      <Dialog open={modalAberto} onClose={fecharModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: "#18316e" }}>
          Detalhes do job
          {jobDetalhe?.id ? ` #${jobDetalhe.id}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: "0.85rem",
              m: 0,
            }}
          >
            {textoDetalheModal()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharModal} variant="contained" sx={{ bgcolor: "#18316e" }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            fontWeight: "bold",
            fontSize: "1rem",
            "& .MuiAlert-icon": { fontSize: "1.5rem" },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProcessamentoRelatorio;
